import React, { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { twJoin, twMerge } from 'tailwind-merge'
import { DataFile } from 'client/store/files/filesReducer'
import { Input } from 'src/editor/shared/ui-lib/input'
import { File05Icon } from 'src/editor/shared/icons/file-05-icon'
import { SearchInputIcon } from 'src/editor/shared/icons/search-input-icon'
import { Trash02Icon } from 'src/editor/shared/icons/trash-02-icon'
import { Loader } from 'src/editor/shared/ui-lib/loader'
import { CheckboxNew } from 'src/editor/shared/ui-lib/checkbox-new'
import { FetchUserFilesFnType } from './types'
import { useFileLibrary } from './hooks/use-file-library'

interface FileLibraryProps {
  fileType?: string
  fetchData: FetchUserFilesFnType
  onClick: (file: DataFile | null) => void
  show: boolean
  files: Record<string, DataFile>
  detailedFile: DataFile | null
  removeFile: (fileId: number) => void
  removingAllowed: boolean
  removingFileId?: number
  isFirstFetchingFiles: boolean
}

export const FileLibrary = ({
  files,
  fileType,
  detailedFile = null,
  fetchData,
  onClick,
  show,
  removeFile,
  removingAllowed,
  removingFileId,
  isFirstFetchingFiles,
}: FileLibraryProps) => {
  const { t } = useTranslation()

  const { isFetching, fileListRef, onSearchChange, searchValue } =
    useFileLibrary({
      files,
      fileType,
      fetchData,
    })

  return (
    <>
      {show && (
        <Input
          type="search"
          placeholder={t('components.core.files.search.placeholder')}
          onChange={onSearchChange}
          value={searchValue}
          endElement={<SearchInputIcon />}
        />
      )}
      <div
        className={twJoin(
          'relative mt-4 max-h-[340px] min-h-[252px]',
          show ? 'flex' : 'hidden',
        )}
      >
        <div
          className={twMerge(
            'relative grid w-full grid-cols-5 gap-x-2 gap-y-4 overflow-y-auto overflow-x-hidden',
            isFetching && 'custom-touch-none pointer-events-none',
          )}
          ref={fileListRef}
        >
          {Object.keys(files)
            .reverse()
            .map(fileId => {
              const file = files[fileId]
              const isImage = file.type === 'image'
              const isThumbnail = !!file.thumbnail
              return (
                <div
                  key={fileId}
                  className="relative flex h-fit flex-col transition-opacity"
                >
                  {!!removingFileId && removingFileId === +fileId ? null : (
                    <CheckboxNew
                      wrapperClassName="checked:bg-transparent absolute top-2 left-1.5 z-10"
                      className="h-5 w-5 bg-bgPrimaryBase"
                      onChange={() => {
                        detailedFile?.id !== +fileId
                          ? onClick(file)
                          : onClick(null)
                      }}
                      checked={detailedFile?.id === +fileId}
                    />
                  )}
                  {isThumbnail && (
                    <FileImage
                      imagePath={file.thumbnail}
                      removingAllowed={removingAllowed}
                      fileId={Number(fileId)}
                      removingFileId={removingFileId}
                      removeFile={removeFile}
                    />
                  )}
                  {isImage && !isThumbnail && (
                    <FileImage
                      imagePath={file.path}
                      removingAllowed={removingAllowed}
                      fileId={Number(fileId)}
                      removingFileId={removingFileId}
                      removeFile={removeFile}
                    />
                  )}
                  {!isImage && !isThumbnail && (
                    <FileImage
                      removingAllowed={removingAllowed}
                      fileId={Number(fileId)}
                      removingFileId={removingFileId}
                      removeFile={removeFile}
                    />
                  )}
                  <span className="mt-2 font-medium text-textTertiary text-xs">
                    {getShortName(file.filenameWithoutHash || file.filename)}
                  </span>
                </div>
              )
            })}
        </div>
        {(isFirstFetchingFiles || isFetching) && (
          <div className="absolute inset-0 z-11 flex items-center justify-center rounded-md bg-black/15">
            <Loader className="h-10 w-10" />
          </div>
        )}
      </div>
    </>
  )
}

interface FileImageProps {
  imagePath?: string
  removingAllowed: boolean
  fileId: number
  removingFileId?: number
  removeFile: (fileId: number) => void
}

function FileImage({
  imagePath,
  removingAllowed,
  fileId,
  removingFileId,
  removeFile,
}: FileImageProps) {
  const [removeButtonVisible, setRemoveButtonVisible] = useState(false)
  const isRemoving = !!removingFileId && removingFileId === fileId

  return (
    <div
      onMouseLeave={() => setRemoveButtonVisible(false)}
      onMouseEnter={() => setRemoveButtonVisible(true)}
    >
      {imagePath ? (
        <img
          src={imagePath}
          alt={imagePath}
          className="h-[70px] w-full rounded-md object-cover"
        />
      ) : (
        <div className="flex h-17.5 w-full items-center justify-center rounded-md bg-bgSecondary">
          <File05Icon width={36} height={36} />
        </div>
      )}
      {removeButtonVisible && removingAllowed && (
        <button
          className="absolute top-2 right-1.5 flex h-6 w-6 cursor-pointer items-center justify-center rounded-[2px] bg-black/30 text-error-500 transition-all"
          type="button"
          disabled={isRemoving}
          onClick={e => {
            e.stopPropagation()
            removeFile(fileId)
          }}
        >
          {isRemoving ? (
            <Loader className="h-3 w-3" />
          ) : (
            <Trash02Icon width={16} className="text-iconError" />
          )}
        </button>
      )}
    </div>
  )
}

export function getShortName(name: string, maxLength = 10, sliceTo = 5) {
  return name.length > maxLength
    ? `${name.slice(0, sliceTo)}...${name.slice(-8)}`
    : name
}
